/**
 * based on:
 * https://tanmaythole.medium.com/optimizing-re-render-in-react-context-d823e113a45d
 */

import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useRef,
  useSyncExternalStore,
} from 'react';

export type StoreData = Record<string, any>;

export interface Store<Data extends StoreData = StoreData> {
  get: () => Data;
  set: (value: Partial<Data> | ((data: Data) => Partial<Data>)) => void;
  subscribe: (callback: () => any, ...keys: (keyof Data)[]) => () => void;
}

export const createStore = <Data extends StoreData = StoreData>() => {
  const StoreContext = createContext<Store<Data>>(undefined!);

  const useSetValue = <Key extends keyof Data>(key: Key) => {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error('useSetValue must be used within a StoreProvider');
    }

    const { set } = store;

    return useCallback(
      (value: Data[Key]) => {
        const partialData: Partial<Data> = {};
        partialData[key] = value;
        set(partialData);
      },
      [key, set],
    );
  };

  const useValue = <Key extends keyof Data>(key: Key) => {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error('useStoreValue must be used within a StoreProvider');
    }

    const { set } = store;

    const value = useSyncExternalStore(
      (callback) => store.subscribe(callback, key),
      () => store.get()[key],
    );

    const setValue = useSetValue(key);

    const computeValue = useCallback(
      (value: (prevValue: Data[Key]) => Data[Key]) => {
        set((prevData) => {
          const prevValue = prevData[key];
          return { ...prevData, [key]: value(prevValue) };
        });
      },
      [key, set],
    );

    return [value, setValue, computeValue] as const;
  };

  const useGetValue = <Key extends keyof Data>(key: Key) => {
    const store = useContext(StoreContext);

    if (!store) {
      throw new Error('useGetValue must be used within a StoreProvider');
    }

    const { get } = store;

    return useCallback(() => get()[key], [get, key]);
  };

  const useOptionalValue = <Key extends keyof Data>(key: Key) => {
    const store = useContext(StoreContext);

    return useSyncExternalStore(
      (callback) => store?.subscribe(callback, key) ?? noop,
      () => store?.get()[key],
    );
  };

  const Provider: FC<PropsWithChildren<{ initialData: Data }>> = ({
    initialData,
    children,
  }) => (
    <StoreContext.Provider value={useNewPubSubStore<Data>(initialData)}>
      {children}
    </StoreContext.Provider>
  );

  return { Provider, useValue, useGetValue, useSetValue, useOptionalValue };
};

const useNewPubSubStore = <Data extends StoreData = StoreData>(
  initialData: Data,
): Store<Data> => {
  const store = useRef<Data>(initialData);

  const get = useCallback(() => store.current, []);

  const set = useCallback(
    (value: Partial<Data> | ((data: Data) => Partial<Data>)) => {
      if (typeof value === 'function') {
        value = value(store.current);
      }

      store.current = { ...store.current, ...value };
      subscribers.current.forEach(([callback, keys]) => {
        if (keys.some((key) => key in value)) {
          callback();
        }
      });
    },
    [],
  );

  const subscribers = useRef(new Set<readonly [() => any, (keyof Data)[]]>());

  const subscribe = useCallback(
    (callback: () => any, ...keys: (keyof Data)[]) => {
      const subscription = [
        callback,
        keys.length ? keys : Object.keys(store.current),
      ] as const;
      subscribers.current.add(subscription);
      return () => {
        subscribers.current.delete(subscription);
      };
    },
    [],
  );

  return { get, set, subscribe };
};

const noop = () => {};
